import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { chat, ApiError, type ChatCitation, type MatchResult } from '../lib/api.js';
import { useFlywheel } from '../lib/flywheel.js';

interface Props {
  agent: MatchResult;
  onWrap: (turns: Turn[]) => void;
  onChangeAgent: () => void;
}

export interface Turn {
  role: 'user' | 'assistant';
  content: string;
  citations?: ChatCitation[];
}

const MAX_TURNS_BEFORE_WRAP_HINT = 6;

/**
 * Buffered chat — POST /chat returns the full reply per turn (no streaming
 * yet; deferred to post-demo per the chat handler's design note). Multi-turn
 * via session_id passed back and forth.
 */
export function Chat({ agent, onWrap, onChangeAgent }: Props) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  // ChatError carries enough context to show a one-click retry without
  // making the user re-type their last message.
  const [chatError, setChatError] = useState<{ message: string; retryable: boolean; lastMessage: string } | null>(null);
  const feedRef = useRef<HTMLDivElement | null>(null);
  const { emit } = useFlywheel();

  // Auto-scroll to bottom on new turns
  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, pending]);

  const sendMessage = async (message: string) => {
    if (!message || pending) return;
    setChatError(null);
    // Optimistically render the user's turn so they see it the moment they
    // hit send. On failure we leave it visible and offer retry.
    setTurns((prev) => {
      // If the last turn is already this exact user message (retry case),
      // don't re-append it.
      const last = prev[prev.length - 1];
      if (last?.role === 'user' && last.content === message) return prev;
      return [...prev, { role: 'user', content: message }];
    });
    setPending(true);
    try {
      const r = await chat({
        agent_id: agent.agent_id,
        ...(sessionId ? { session_id: sessionId } : {}),
        message,
      });
      setSessionId(r.session_id);
      setTurns((prev) => [
        ...prev,
        { role: 'assistant', content: r.reply, citations: r.citations },
      ]);
      // Each turn is the candidate consuming what the alumni left behind.
      // Pulse from candidate → alumni so the loop visually closes per turn.
      emit({
        kind: 'MESSAGE_SENT',
        actor: 'candidate',
        flowsTo: 'alumni',
        note: `${agent.display_name}と対話中`,
      });
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      setChatError({
        message: apiErr?.userMessage ?? (err instanceof Error ? err.message : String(err)),
        retryable: apiErr?.retryable ?? false,
        lastMessage: message,
      });
    } finally {
      setPending(false);
    }
  };

  const send = () => {
    const message = draft.trim();
    if (!message) return;
    setDraft('');
    void sendMessage(message);
  };

  const retry = () => {
    if (chatError?.lastMessage) void sendMessage(chatError.lastMessage);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl+Enter to send; plain Enter still inserts a newline so Japanese
    // IME composition isn't disrupted.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void send();
    }
  };

  const turnsForWrap = turns.length >= MAX_TURNS_BEFORE_WRAP_HINT;
  const canWrap = turns.length >= 2; // at least one round-trip

  return (
    <div className="chat-shell">
      <div className="chat-header">
        <div className="avatar avatar-xs">{agent.display_name.slice(0, 1)}</div>
        <div className="chat-id">
          <div className="match-name">
            <h2>{agent.display_name}</h2>
            {agent.type === 'synthetic' && (
              <span className="virtual-badge">仮想キャラクター</span>
            )}
          </div>
          <p>{agent.headline}</p>
        </div>
        <button type="button" className="btn-ghost" onClick={onChangeAgent}>
          別の先輩
        </button>
      </div>

      <div className="chat-feed" ref={feedRef}>
        {turns.length === 0 && (
          <p className="muted">
            気になっていることをそのまま書いてみてください。
            英語が不安、進路が決まらない、起業が怖い、なんでも。
          </p>
        )}
        {turns.map((t, i) => (
          <div key={i} className={t.role === 'user' ? 'bubble bubble-user' : 'bubble bubble-assistant'}>
            <div>{t.content}</div>
            {t.role === 'assistant' && t.citations && t.citations.length > 0 && (
              <div className="citations">
                {t.citations.map((c) =>
                  c.source_url ? (
                    <a
                      key={c.log_id}
                      className="citation-chip"
                      href={c.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={c.day_label ?? c.log_id}
                    >
                      {c.day_label ?? c.log_id}
                    </a>
                  ) : (
                    <span key={c.log_id} className="citation-chip">
                      {c.day_label ?? c.log_id}
                    </span>
                  ),
                )}
              </div>
            )}
          </div>
        ))}
        {pending && (
          <div className="thinking">
            <span /> <span /> <span />
          </div>
        )}
        {chatError && !pending && (
          <div className="error">
            <div className={chatError.retryable ? 'error-message' : ''}>{chatError.message}</div>
            {chatError.retryable && (
              <button type="button" className="btn btn-inline" onClick={retry}>
                もう一度送る
              </button>
            )}
          </div>
        )}
      </div>

      <div className="chat-input">
        <textarea
          className="textarea"
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="メッセージ(⌘+Enter で送信)"
          disabled={pending}
        />
        <button type="button" className="btn" onClick={send} disabled={pending || !draft.trim()}>
          送信
        </button>
        {canWrap && (
          <button
            type="button"
            className="btn-ghost"
            onClick={() => onWrap(turns)}
            title={turnsForWrap ? '十分話したらまとめましょう' : '対話を終えてまとめる'}
          >
            まとめる
          </button>
        )}
      </div>
    </div>
  );
}
